import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@frontend/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@frontend/components/ui/select';
import { useDocumentUpload } from '@frontend/features/documents/hooks/useDocumentUpload';
import { useFolders } from '@frontend/features/documents/hooks/useFolders';
import { CheckCircle2, FileText, Upload, X, XCircle } from 'lucide-react';

export function GlobalDropZone() {
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [failedFiles, setFailedFiles] = useState<string[]>([]);
  const [uploadDone, setUploadDone] = useState(false);
  const dragCounter = useRef(0);

  const { folders } = useFolders(null);
  const { uploadDocument, uploadProgress } = useDocumentUpload();

  // Auto-select first folder when none is selected
  const effectiveFolderId = useMemo(
    () => selectedFolderId || (folders.length > 0 ? folders[0]!.id : ''),
    [selectedFolderId, folders],
  );

  const handleDragEnter = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      dragCounter.current++;
      if (e.dataTransfer?.types.includes('Files') && !droppedFiles.length) {
        setIsDragging(true);
      }
    },
    [droppedFiles.length],
  );

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      dragCounter.current = 0;
      setIsDragging(false);

      if (droppedFiles.length) return;

      const files = Array.from(e.dataTransfer?.files ?? []).filter((f) =>
        /\.(pdf|docx|pptx|txt)$/i.test(f.name),
      );

      if (files.length > 0) {
        setDroppedFiles(files);
        setUploadDone(false);
        setCompletedCount(0);
        setFailedFiles([]);
      }
    },
    [droppedFiles.length],
  );

  useEffect(() => {
    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  const handleUpload = async () => {
    if (!effectiveFolderId || droppedFiles.length === 0) return;

    setIsUploading(true);
    setCompletedCount(0);
    setFailedFiles([]);
    const failed: string[] = [];

    for (const file of droppedFiles) {
      try {
        await uploadDocument({ file, folderId: effectiveFolderId });
        setCompletedCount((prev) => prev + 1);
      } catch {
        failed.push(file.name);
        setFailedFiles([...failed]);
      }
    }

    setIsUploading(false);
    setUploadDone(true);

    if (failed.length === 0) {
      setTimeout(() => handleClose(), 2000);
    }
  };

  const handleClose = () => {
    setDroppedFiles([]);
    setIsDragging(false);
    setIsUploading(false);
    setUploadDone(false);
    setCompletedCount(0);
    setFailedFiles([]);
  };

  // Full-screen drag overlay
  if (isDragging) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div className="border-2 border-dashed border-primary rounded-2xl p-16 text-center">
          <Upload className="w-16 h-16 text-primary mx-auto mb-4" />
          <p className="text-xl font-semibold">Drop files to upload</p>
          <p className="text-sm text-muted-foreground mt-1">
            PDF, DOCX, PPTX, TXT
          </p>
        </div>
      </div>
    );
  }

  // Upload panel after drop
  if (droppedFiles.length > 0 || uploadDone) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
        <div className="bg-background rounded-xl shadow-lg border w-full max-w-md mx-4">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-base font-semibold">
              Upload{' '}
              {droppedFiles.length || completedCount + failedFiles.length} file
              {(droppedFiles.length || completedCount + failedFiles.length) !==
              1
                ? 's'
                : ''}
            </h2>
            {!isUploading && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleClose}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="p-4 space-y-4">
            {/* File list */}
            {!isUploading && !uploadDone && (
              <>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {droppedFiles.map((file, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-sm text-muted-foreground py-1"
                    >
                      <FileText className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{file.name}</span>
                      <span className="text-xs ml-auto shrink-0">
                        {(file.size / 1024 / 1024).toFixed(1)} MB
                      </span>
                    </div>
                  ))}
                </div>

                {/* Folder picker */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Upload to folder
                  </label>
                  <Select
                    value={effectiveFolderId}
                    onValueChange={setSelectedFolderId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a folder" />
                    </SelectTrigger>
                    <SelectContent>
                      {folders.map((folder) => (
                        <SelectItem key={folder.id} value={folder.id}>
                          {folder.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {folders.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      No folders available. Create one first.
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Uploading state */}
            {isUploading && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Upload className="w-5 h-5 animate-pulse text-primary shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      Uploading {completedCount + 1} of {droppedFiles.length}...
                    </p>
                    <div className="w-full bg-muted rounded-full h-2 mt-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Done state */}
            {uploadDone && failedFiles.length === 0 && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-950 text-green-900 dark:text-green-100">
                <CheckCircle2 className="w-5 h-5" />
                <p className="text-sm font-medium">
                  {completedCount} document{completedCount !== 1 ? 's' : ''}{' '}
                  uploaded!
                </p>
              </div>
            )}

            {uploadDone && failedFiles.length > 0 && (
              <div className="space-y-2">
                {completedCount > 0 && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-950 text-green-900 dark:text-green-100">
                    <CheckCircle2 className="w-5 h-5" />
                    <p className="text-sm font-medium">
                      {completedCount} uploaded
                    </p>
                  </div>
                )}
                <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-950 text-red-900 dark:text-red-100">
                  <XCircle className="w-5 h-5 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">
                      {failedFiles.length} failed
                    </p>
                    <ul className="text-xs mt-1 space-y-0.5">
                      {failedFiles.map((name) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {!uploadDone && (
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={
                  !effectiveFolderId ||
                  droppedFiles.length === 0 ||
                  isUploading ||
                  folders.length === 0
                }
              >
                {isUploading ? (
                  <>
                    <Upload className="w-4 h-4 mr-2 animate-pulse" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </div>
          )}

          {uploadDone && (
            <div className="flex justify-end p-4 border-t">
              <Button onClick={handleClose}>Close</Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}
